import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { trainingRecordsService } from '../../../services/growthService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
};

const ExpiryBadge = ({ expiryDate }) => {
  const days = getDaysUntilExpiry(expiryDate);
  if (days === null) return null;
  if (days < 0) return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-red-100 text-red-700"><Icon name="AlertTriangle" size={12} />Expired</span>;
  if (days <= 7) return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-red-100 text-red-700"><Icon name="AlertTriangle" size={12} />{days}d left</span>;
  if (days <= 30) return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700"><Icon name="Clock" size={12} />{days}d left</span>;
  return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700"><Icon name="CheckCircle2" size={12} />{days}d left</span>;
};

const statusConfig = {
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'CheckCircle2' },
  'in-progress': { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'Clock' },
  required: { bg: 'bg-red-100', text: 'text-red-700', icon: 'AlertCircle' },
  pending: { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'Circle' },
};

const PartnerTrainingRecords = () => {
  const toast = useToast();
  const [records, setRecords] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterExpiry, setFilterExpiry] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [recs, pts] = await Promise.all([trainingRecordsService?.getAll(), partnerService?.getAll()]);
      setRecords(recs || []);
      setPartners(pts || []);
    } catch (err) { toast?.error('Failed to load training records.'); }
    finally { setLoading(false); }
  };

  const filtered = records?.filter(r => {
    if (filterPartner !== 'all' && r?.partner_id !== filterPartner) return false;
    if (filterStatus !== 'all' && r?.training_status !== filterStatus) return false;
    if (search && !r?.name?.toLowerCase()?.includes(search?.toLowerCase())) return false;
    if (filterExpiry === 'expiring_soon') {
      const days = getDaysUntilExpiry(r?.expiry_date);
      if (days === null || days > 30 || days < 0) return false;
    }
    if (filterExpiry === 'expired') {
      const days = getDaysUntilExpiry(r?.expiry_date);
      if (days === null || days >= 0) return false;
    }
    return true;
  });

  const expiringCount = records?.filter(r => { const d = getDaysUntilExpiry(r?.expiry_date); return d !== null && d >= 0 && d <= 30; })?.length;
  const expiredCount = records?.filter(r => { const d = getDaysUntilExpiry(r?.expiry_date); return d !== null && d < 0; })?.length;
  const completedCount = records?.filter(r => r?.training_status === 'completed')?.length;
  const requiredCount = records?.filter(r => r?.training_status === 'required')?.length;

  return (
    <div className="space-y-20">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex items-center gap-14 mb-20">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <Icon name="GraduationCap" size={22} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Partner Training Records</h2>
            <p className="text-sm text-slate-500 mt-1">Track all partner training progress with expiry alerts</p>
          </div>
        </div>

        {/* Alert Banner */}
        {(expiringCount > 0 || expiredCount > 0) && (
          <div className="flex flex-wrap gap-12 mb-20">
            {expiredCount > 0 && (
              <div className="flex items-center gap-10 px-16 py-10 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
                <Icon name="AlertTriangle" size={16} />{expiredCount} training{expiredCount > 1 ? 's' : ''} expired — immediate renewal required
              </div>
            )}
            {expiringCount > 0 && (
              <div className="flex items-center gap-10 px-16 py-10 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm font-medium">
                <Icon name="Clock" size={16} />{expiringCount} training{expiringCount > 1 ? 's' : ''} expiring within 30 days
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-12">
          <div className="relative flex-1 min-w-[180px]">
            <Icon name="Search" size={16} className="absolute left-14 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search records..." className="w-full pl-40 pr-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" />
          </div>
          <select value={filterPartner} onChange={e => setFilterPartner(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white">
            <option value="all">All Partners</option>
            {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white">
            <option value="all">All Statuses</option>
            {['required', 'in-progress', 'completed', 'pending']?.map(s => <option key={s} value={s}>{s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
          </select>
          <select value={filterExpiry} onChange={e => setFilterExpiry(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white">
            <option value="all">All Expiry</option>
            <option value="expiring_soon">Expiring Soon (30d)</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-16">
        {[
          { label: 'Total Records', value: records?.length, icon: 'FileText', color: 'from-slate-500 to-slate-700', onClick: () => setFilterStatus('all') },
          { label: 'Completed', value: completedCount, icon: 'CheckCircle2', color: 'from-emerald-500 to-teal-600', onClick: () => setFilterStatus('completed') },
          { label: 'Required', value: requiredCount, icon: 'AlertCircle', color: 'from-red-500 to-rose-600', onClick: () => setFilterStatus('required') },
          { label: 'Expiring Soon', value: expiringCount, icon: 'Clock', color: 'from-amber-500 to-orange-600', onClick: () => setFilterExpiry('expiring_soon') },
        ]?.map((stat, i) => (
          <button key={i} onClick={stat?.onClick} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20 text-left hover:border-slate-300 transition-all">
            <div className="flex items-center justify-between mb-12">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat?.color} flex items-center justify-center`}><Icon name={stat?.icon} size={18} className="text-white" /></div>
              <span className="text-2xl font-bold text-slate-900">{stat?.value}</span>
            </div>
            <p className="text-xs text-slate-500 font-medium">{stat?.label}</p>
          </button>
        ))}
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="px-24 py-16 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">{filtered?.length} record{filtered?.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-48"><div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" /></div>
        ) : filtered?.length === 0 ? (
          <div className="p-48 text-center"><Icon name="GraduationCap" size={40} className="text-slate-300 mx-auto mb-16" /><p className="text-slate-500 font-medium">No training records found</p></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered?.map(record => {
              const cfg = statusConfig?.[record?.training_status] || statusConfig?.pending;
              return (
                <div key={record?.id} className="px-24 py-18 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-start justify-between gap-16">
                    <div className="flex items-start gap-14 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-lg ${cfg?.bg} flex items-center justify-center flex-shrink-0 mt-1`}>
                        <Icon name={cfg?.icon} size={16} className={cfg?.text} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-8 mb-6">
                          <h3 className="text-sm font-semibold text-slate-900">{record?.name}</h3>
                          {record?.expiry_date && <ExpiryBadge expiryDate={record?.expiry_date} />}
                        </div>
                        {record?.description && <p className="text-xs text-slate-500 mb-8 line-clamp-1">{record?.description}</p>}
                        <div className="flex flex-wrap items-center gap-12 text-xs text-slate-500">
                          {record?.partners && <span className="flex items-center gap-5"><Icon name="Building2" size={12} />{record?.partners?.brand_name || record?.partners?.legal_name}</span>}
                          {record?.duration && <span className="flex items-center gap-5"><Icon name="Clock" size={12} />{record?.duration}</span>}
                          {record?.participants > 0 && <span className="flex items-center gap-5"><Icon name="Users" size={12} />{record?.participants} participants</span>}
                          {record?.expiry_date && <span className="flex items-center gap-5"><Icon name="Calendar" size={12} />Expires {new Date(record?.expiry_date)?.toLocaleDateString()}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-8 flex-shrink-0">
                      <span className={`px-10 py-4 rounded-lg text-xs font-semibold ${cfg?.bg} ${cfg?.text}`}>
                        {record?.training_status?.charAt(0)?.toUpperCase() + record?.training_status?.slice(1)?.replace('-', ' ')}
                      </span>
                      {record?.training_status === 'in-progress' && record?.progress > 0 && (
                        <div className="flex items-center gap-8">
                          <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${record?.progress}%` }} /></div>
                          <span className="text-xs font-semibold text-amber-600">{record?.progress}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerTrainingRecords;
